
// SEO utility functions for dynamic meta tag updates
export interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  structuredData?: object;
}

export const updateSEO = (seoData: SEOData) => {
  // Update title
  document.title = seoData.title;
  
  // Update meta description
  updateMetaTag('name', 'description', seoData.description);
  
  // Update keywords
  updateMetaTag('name', 'keywords', seoData.keywords);
  
  // Update canonical URL
  updateLinkTag('canonical', seoData.canonical);
  
  // Update last-modified
  updateMetaTag('name', 'last-modified', new Date().toISOString().split('T')[0]);
  
  // Update Open Graph tags
  if (seoData.ogTitle) {
    updateMetaTag('property', 'og:title', seoData.ogTitle);
  }
  if (seoData.ogDescription) {
    updateMetaTag('property', 'og:description', seoData.ogDescription);
  }
  if (seoData.ogUrl) {
    updateMetaTag('property', 'og:url', seoData.ogUrl);
  }
  
  // Update Twitter Card tags
  if (seoData.ogTitle) {
    updateMetaTag('name', 'twitter:title', seoData.ogTitle);
  }
  if (seoData.ogDescription) {
    updateMetaTag('name', 'twitter:description', seoData.ogDescription);
  }
  
  // Update structured data
  if (seoData.structuredData) {
    updateStructuredData(seoData.structuredData);
  }
};

const updateMetaTag = (attribute: string, name: string, content: string) => {
  let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  if (element) {
    element.content = content;
  } else {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    element.content = content;
    document.head.appendChild(element);
  }
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (element) {
    element.href = href;
  } else {
    element = document.createElement('link');
    element.rel = rel;
    element.href = href;
    document.head.appendChild(element);
  }
};

const updateStructuredData = (data: object) => {
  let script = document.querySelector('script[type="application/ld+json"]');
  if (script) {
    script.textContent = JSON.stringify(data);
  } else {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }
};

// SEO data for each page
export const seoData = {
  home: {
    title: "Henatos Limited - Henry Adebisi CFO | Africa's Trusted Partner in Hides and Skins | Premium Air-Dried Cow Hides Nigeria",
    description: "Henatos Limited, co-founded by Henry Adebisi (CFO) and Tosin Adekoya (COO), is Nigeria's leading international commodities company specializing in premium air-dried cow hides from East and Southern Africa. Reliable sourcing, quality assurance, and cross-border trade across Tanzania, Burundi, Zambia, Zimbabwe for Pomo Ijebu suppliers.",
    keywords: "Henry Adebisi CFO, Henatos Limited founder, dried cow hides Africa, hide export Nigeria, Tosin Adekoya COO, Pomo Ijebu supplier, air-dried hides Tanzania",
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/`,
    ogTitle: "Henatos Limited - Henry Adebisi CFO | Africa's Trusted Partner in Hides and Skins",
    ogDescription: "Premium air-dried cow hides sourcing from East and Southern Africa to Nigeria. Co-founded by Henry Adebisi (CFO) and Tosin Adekoya (COO). Building Africa's most reliable trade network.",
    ogUrl: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Henatos Limited",
      "description": "Nigeria-based international commodities company specializing in air-dried cow hides and skins, co-founded by Henry Adebisi and Tosin Adekoya",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}`,
      "logo": `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/logo.jpg`,
      "founder": [
        {
          "@type": "Person",
          "name": "Henry Adebisi",
          "jobTitle": "Co-Founder and Chief Financial Officer",
          "worksFor": {
            "@type": "Organization",
            "name": "Henatos Limited"
          },
          "description": "Co-Founder and CFO of Henatos Limited, Nigeria's leading hides and skins trading company"
        },
        {
          "@type": "Person",
          "name": "Tosin Adekoya",
          "jobTitle": "Co-Founder and Chief Operations Officer",
          "worksFor": {
            "@type": "Organization",
            "name": "Henatos Limited"
          }
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "86 Colledge Road, Oke Alafia, Oke Agbo",
        "addressLocality": "Ijebu Igbo",
        "addressRegion": "Ogun State",
        "addressCountry": "Nigeria"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "accounts@henatosbiz.com",
        "contactType": "customer service"
      },
      "areaServed": ["Nigeria", "Tanzania", "Burundi", "Rwanda", "Zambia", "Zimbabwe"],
      "foundingDate": "2020",
      "industry": "International Trade",
      "numberOfEmployees": "10-50"
    }
  },
  about: {
    title: "About Henry Adebisi & Tosin Adekoya | Henatos Limited Founders | Nigeria's Leading Hides Trading Company",
    description: "Learn about Henatos Limited's co-founders Henry Adebisi (CFO) and Tosin Adekoya (COO) and their journey from local traders to pan-African enterprise leaders. Our mission, vision, leadership team, and commitment to building Africa's most reliable hides and skins trade network across Nigeria, Tanzania, Burundi, Zambia, Zimbabwe.",
    keywords: "Henry Adebisi CFO, Tosin Adekoya COO, Henatos Limited founders, African trade network, hides skins trading, Nigeria commodities, cross-border trade",
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/about`,
    ogTitle: "About Henry Adebisi & Tosin Adekoya - Henatos Limited Founders",
    ogDescription: "Discover the journey of Henry Adebisi (CFO) and Tosin Adekoya (COO) from local traders to pan-African enterprise leaders specializing in premium hides and skins.",
    ogUrl: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/about`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Henatos Limited Founders",
      "description": "Learn about Henry Adebisi and Tosin Adekoya's journey from local traders to pan-African enterprise leaders",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Henatos Limited",
        "foundingDate": "2020",
        "founder": [
          {
            "@type": "Person",
            "name": "Henry Adebisi",
            "jobTitle": "Co-Founder & Chief Financial Officer",
            "description": "Henry Adebisi is the Co-Founder and CFO of Henatos Limited, Nigeria's leading international commodities company specializing in hides and skins trading across Africa",
            "worksFor": {
              "@type": "Organization",
              "name": "Henatos Limited"
            },
            "knowsAbout": ["International Trade", "Financial Management", "African Commodities", "Cross-border Commerce"],
            "alumniOf": "Business Leadership",
            "nationality": "Nigerian"
          },
          {
            "@type": "Person", 
            "name": "Tosin Adekoya",
            "jobTitle": "Co-Founder & Chief Operations Officer",
            "description": "Tosin Adekoya is the Co-Founder and COO of Henatos Limited, overseeing operations across East and Southern Africa",
            "worksFor": {
              "@type": "Organization",
              "name": "Henatos Limited"
            }
          }
        ]
      }
    }
  },
  products: {
    title: "Premium Air-Dried Cow Hides by Henry Adebisi's Henatos Limited | Export-Grade Quality Standards Nigeria",
    description: "Discover Henatos Limited's premium air-dried cow hides (6.5kg-12kg) with export-grade quality standards. Under the leadership of CFO Henry Adebisi and COO Tosin Adekoya, specialized processing for Pomo Ijebu production, rigorous quality control, professional grading, and reliable logistics from East and Southern Africa to Nigeria.",
    keywords: "Henry Adebisi Henatos products, air-dried cow hides, export grade hides, Pomo Ijebu materials, quality cow skins, Nigeria hide import",
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/products`,
    ogTitle: "Premium Air-Dried Cow Hides by Henry Adebisi's Henatos Limited",
    ogDescription: "Specialized air-dried cow hides with rigorous quality standards for Nigerian market, led by CFO Henry Adebisi.",
    ogUrl: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/products`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Premium Air-Dried Cow Hides",
      "description": "Export-grade air-dried cow hides averaging 6.5kg-12kg per piece, processed for Pomo Ijebu production under the leadership of Henry Adebisi (CFO) and Tosin Adekoya (COO)",
      "brand": {
        "@type": "Brand",
        "name": "Henatos Limited"
      },
      "manufacturer": {
        "@type": "Organization",
        "name": "Henatos Limited",
        "founder": [
          {
            "@type": "Person",
            "name": "Henry Adebisi",
            "jobTitle": "Co-Founder & Chief Financial Officer"
          }
        ]
      },
      "category": "Agricultural Products",
      "weight": "6.5-12 kg",
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Processing Method",
          "value": "Air-dried only, no salt treatment"
        },
        {
          "@type": "PropertyValue", 
          "name": "Moisture Content",
          "value": "8-12%"
        },
        {
          "@type": "PropertyValue",
          "name": "Origin",
          "value": "Tanzania, Burundi, Zambia, Zimbabwe"
        }
      ]
    }
  },
  suppliers: {
    title: "Partner with Henry Adebisi's Henatos Limited | Become a Supplier | Advance Deposits & Steady Offtake Africa",
    description: "Join Henry Adebisi and Tosin Adekoya's trusted supplier network across East and Southern Africa. Benefit from advance deposits, steady offtake agreements, technical support, and quality assurance programs. Partner with Africa's most reliable hides and skins trading company led by experienced founders.",
    keywords: "Henry Adebisi supplier partnership, become supplier Africa, hides supplier partnership, advance deposits suppliers, steady offtake agreement, supplier network",
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/suppliers`,
    ogTitle: "Partner with Henry Adebisi's Henatos - Become a Trusted Supplier",
    ogDescription: "Join our network with advance deposits, steady offtake, and comprehensive support under Henry Adebisi's leadership.",
    ogUrl: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/suppliers`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Supplier Partnership Program",
      "description": "Join Henry Adebisi and Tosin Adekoya's trusted supplier network with advance deposits and steady offtake",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/suppliers`,
      "mainEntity": {
        "@type": "Service",
        "name": "Supplier Partnership Program",
        "provider": {
          "@type": "Organization",
          "name": "Henatos Limited",
          "founder": [
            {
              "@type": "Person",
              "name": "Henry Adebisi",
              "jobTitle": "Co-Founder & Chief Financial Officer"
            }
          ]
        },
        "areaServed": ["Tanzania", "Burundi", "Rwanda", "Zambia", "Zimbabwe"],
        "serviceType": "Agricultural Supply Chain"
      }
    }
  },
  buyers: {
    title: "Nigerian Buyers | Henry Adebisi's Henatos Limited | Reliable Hides Supply | Import Documentation Support",
    description: "Henry Adebisi and Tosin Adekoya's Henatos Limited provides Nigerian and ECOWAS buyers with consistent supply of export-grade air-dried cow hides. Complete import documentation support, customs clearance assistance, competitive pricing, and reliable 18-25 day transit times to Lagos ports.",
    keywords: "Henry Adebisi Nigerian buyers, Nigerian hides buyer, import cow hides Nigeria, Lagos port clearance, ECOWAS hides supply, reliable hides supplier",
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/buyers`,
    ogTitle: "Reliable Supply for Nigerian Buyers - Henry Adebisi's Henatos Limited",
    ogDescription: "Consistent supply of export-grade hides with full documentation and logistics support under Henry Adebisi's leadership.",
    ogUrl: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/buyers`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Buyer Services",
      "description": "Reliable supply of export-grade hides for Nigerian and ECOWAS buyers by Henry Adebisi's Henatos Limited",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/buyers`,
      "mainEntity": {
        "@type": "Service",
        "name": "Export-Grade Hides Supply",
        "provider": {
          "@type": "Organization",
          "name": "Henatos Limited",
          "founder": [
            {
              "@type": "Person",
              "name": "Henry Adebisi",
              "jobTitle": "Co-Founder & Chief Financial Officer"
            }
          ]
        },
        "areaServed": ["Nigeria", "Ghana", "Benin", "Togo", "Burkina Faso"],
        "serviceType": "International Trade"
      }
    }
  },
  contact: {
    title: "Contact Henry Adebisi | Henatos Limited CFO | Nigeria Headquarters | Ijebu Igbo Ogun State",
    description: "Contact Henry Adebisi (CFO) and Tosin Adekoya (COO) at Henatos Limited's Nigeria headquarters in Ijebu Igbo, Ogun State. Get in touch for supplier partnerships, buyer inquiries, or general questions about our pan-African hides and skins trading operations. Email: accounts@henatosbiz.com",
    keywords: "contact Henry Adebisi, Henatos Limited CFO contact, Nigeria office address, Ijebu Igbo location, hides trading contact, supplier inquiry",
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/contact`,
    ogTitle: "Contact Henry Adebisi - Henatos Limited CFO",
    ogDescription: "Contact Henry Adebisi (CFO) and our Nigeria headquarters for partnerships and inquiries.",
    ogUrl: `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/contact`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Henry Adebisi - Henatos Limited",
      "description": "Contact information for Henry Adebisi (CFO) and Henatos Limited Nigeria headquarters",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://henatosbiz.com'}/contact`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Henatos Limited",
        "founder": [
          {
            "@type": "Person",
            "name": "Henry Adebisi",
            "jobTitle": "Co-Founder & Chief Financial Officer",
            "description": "Henry Adebisi is the CFO and Co-Founder of Henatos Limited, Nigeria's leading hides and skins trading company"
          }
        ],
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "86 Colledge Road, Oke Alafia, Oke Agbo",
          "addressLocality": "Ijebu Igbo",
          "addressRegion": "Ogun State",
          "addressCountry": "Nigeria"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "accounts@henatosbiz.com",
          "contactType": "customer service"
        }
      }
    }
  }
};
